import memoize from 'memoize-one';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { EditorAppearance } from '@atlaskit/editor-common/types';

import type { ExternalObserver } from '@atlassian/editor-referentiality';
import { createReferentiality } from '@atlassian/editor-referentiality';
import type { ReferentialityAPIOptions } from '@atlassian/editor-referentiality/types';

import { getEditorAnalyticsAPI } from '@confluence/editor-features';
import { expVal } from '@confluence/feature-experiments';

import { MACRO_INTERACTION_BUTTON_UPDATES, MACRO_INTERACTION_DESIGN_UPDATES } from './featureFlags';
import { getConnectDataListener } from './connectDataListener';

type ReferentialityPluginOptions = ReferentialityAPIOptions & {
	appearance?: EditorAppearance;
	externalObservers?: ExternalObserver[];
};

export type ReferentialityContext = {
	api: ReturnType<typeof createReferentiality>['api'] | null;
	plugin: any;
	options: ReferentialityPluginOptions;
};

export const getReferentialityContext = memoize(
	(
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		contentId: string,
		isLivePage: boolean,
		appearance: EditorAppearance = 'full-page',
	): ReferentialityContext => {
		const editorAnalyticsAPI = getEditorAnalyticsAPI(contentId, createAnalyticsEvent);

		const options: ReferentialityPluginOptions = {
			editorAnalyticsAPI,
			appearance,
			featureFlags: {
				[MACRO_INTERACTION_DESIGN_UPDATES]: isLivePage
					? true
					: expVal('confluence_frontend_macro_interaction_design', 'isEnabled', false),
				[MACRO_INTERACTION_BUTTON_UPDATES]: isLivePage
					? true
					: expVal('confluence_live_macros_config_button_updates', 'isEnabled', false),
			},
			externalObservers: [getConnectDataListener()],
		};

		return { ...createReferentiality(options), options };
	},
);
